import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['button', 'icon'];
  static values = { cookieKey: String, sidebarSelector: String, accountSelector: String, footerSelector: String };

  connect() {
    this.maximized = false;
    this.minimized = this.element.classList.contains('is-minimized');
    try {
      const cookie = document.cookie.split('; ').find((item) => item.startsWith(`${encodeURIComponent(this.cookieKeyValue)}=`));
      const position = JSON.parse(decodeURIComponent(cookie?.split('=')[1] || 'null'));
      if (Number.isFinite(position?.x) && Number.isFinite(position?.y)) this.position = position;
    } catch {
      // Invalid or unavailable cookies should not prevent moving the window.
    }
    this.observer = new ResizeObserver(() => this.constrain());
    this.observer.observe(this.element);
    for (const selector of [this.sidebarSelectorValue, this.accountSelectorValue, this.footerSelectorValue]) {
      const anchor = selector && document.querySelector(selector);
      if (anchor) this.observer.observe(anchor);
    }
    this.refresh();
  }

  disconnect() {
    this.observer?.disconnect();
    this.animation?.cancel();
    this.stop();
  }

  prepare() {
    this.previousRect = this.element.getBoundingClientRect();
    this.animation?.cancel();
  }

  change({ detail }) {
    this.minimized = detail.minimized;
    if (this.minimized) this.maximized = false;
    this.refresh();
  }

  refresh() {
    this.element.classList.toggle('is-maximized', this.maximized);
    this.#syncControls();
    this.constrain();
    const from = this.previousRect;
    this.previousRect = null;
    if (!from || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const to = this.element.getBoundingClientRect();
    this.animation = this.element.animate([
      { transform: `translate(${from.x - to.x}px, ${from.y - to.y}px) scale(${from.width / to.width}, ${from.height / to.height})` },
      { transform: 'none' }
    ], { duration: 200, easing: 'ease-out' });
  }

  start(event) {
    if (this.maximized || this.minimized || event.button !== 0 || event.target.closest('a, button, input, select, textarea')) return;
    this.animation?.cancel();
    const rect = this.element.getBoundingClientRect();
    this.drag = { id: event.pointerId, handle: event.currentTarget, x: event.clientX - rect.left, y: event.clientY - rect.top };
    event.currentTarget.setPointerCapture(event.pointerId);
    this.element.classList.add('is-dragging');
    event.preventDefault();
  }

  move(event) {
    if (!this.drag || event.pointerId !== this.drag.id) return;
    this.position = { x: event.clientX - this.drag.x, y: event.clientY - this.drag.y };
    this.constrain(false);
  }

  stop() {
    const drag = this.drag;
    this.drag = null;
    if (drag?.handle.hasPointerCapture(drag.id)) drag.handle.releasePointerCapture(drag.id);
    this.element.classList.remove('is-dragging');
    if (drag) this.#save();
  }

  toggle() {
    this.stop();
    this.prepare();
    this.maximized = !this.maximized;
    if (this.maximized) {
      this.dispatch('maximize');
    } else {
      this.refresh();
    }
  }

  restore() {
    if (!this.maximized) return;
    this.prepare();
    this.maximized = false;
    this.refresh();
  }

  constrain(save = true) {
    if (!this.element.getClientRects().length) return;
    const width = document.documentElement.clientWidth;
    const height = window.innerHeight;
    const style = getComputedStyle(this.element);
    const inset = (edge) => parseFloat(style.getPropertyValue(`--floating-window-safe-${edge}`)) || 0;
    const left = inset('left');
    const top = inset('top');
    const right = width - inset('right');
    const bottom = height - inset('bottom');
    const usableWidth = Math.max(0, right - left);
    const usableHeight = Math.max(0, bottom - top);
    const sidebar = document.querySelector(this.sidebarSelectorValue);
    const sidebarRect = sidebar?.getBoundingClientRect();
    const desktopSidebar = sidebarRect?.right > 0 && getComputedStyle(sidebar).position !== 'fixed';
    const account = desktopSidebar && document.querySelector(this.accountSelectorValue)?.getBoundingClientRect();
    const footerHeight = document.querySelector(this.footerSelectorValue)?.getBoundingClientRect().height || 0;
    const normalWidth = Math.min(320, usableWidth);
    const normalHeight = Math.min(224, usableHeight);
    const dockWidth = desktopSidebar ? sidebarRect.right - left : 280;
    const windowWidth = this.maximized ? usableWidth : this.minimized ? Math.min(dockWidth, usableWidth) : normalWidth;
    const windowHeight = this.maximized ? usableHeight : this.minimized ? Math.min(44, usableHeight) : normalHeight;

    this.element.style.width = `${windowWidth}px`;
    this.element.style.height = `${windowHeight}px`;

    let position;
    if (this.maximized) {
      position = { x: left, y: top };
    } else if (this.minimized) {
      position = { x: left, y: Math.max(top, Math.min(account?.top ?? bottom, bottom) - windowHeight) };
    } else {
      const desired = this.position || { x: (desktopSidebar ? sidebarRect.right : left) + 8, y: Math.min(height - footerHeight, bottom) - normalHeight - 8 };
      position = {
        x: Math.max(left, Math.min(desired.x, right - normalWidth)),
        y: Math.max(top, Math.min(desired.y, bottom - normalHeight))
      };
      if (this.position) {
        this.position = position;
        if (save && !this.drag) this.#save();
      }
    }
    this.element.style.left = `${position.x}px`;
    this.element.style.top = `${position.y}px`;
    this.element.style.right = 'auto';
    this.element.style.bottom = 'auto';
  }

  #syncControls() {
    const label = this.maximized ? 'Exit full-screen preview' : 'Full-screen preview';
    this.buttonTarget.setAttribute('aria-label', label);
    this.buttonTarget.setAttribute('title', label);
    this.buttonTarget.setAttribute('aria-pressed', String(this.maximized));
    this.iconTarget.classList.toggle('fa-expand', !this.maximized);
    this.iconTarget.classList.toggle('fa-compress', this.maximized);
  }

  #save() {
    if (!this.position) return;
    try {
      document.cookie = `${encodeURIComponent(this.cookieKeyValue)}=${encodeURIComponent(JSON.stringify(this.position))}; Path=/; Max-Age=31536000; SameSite=Lax`;
    } catch {
      // Keep the current position even when cookies are unavailable.
    }
  }
}
